export type CheckpointLayoutInput = {
  targets: number[]
  max: number
  width: number
  /** Índice do próximo marco — priorizado no viewport. */
  focusIndex: number
  markerSizePx: number
  minCenterGapPx: number
}

export type CheckpointLayout = {
  /** Centro de cada marco em px, relativo à trilha. */
  positions: number[]
  /** Deslocamento horizontal do layer de checkpoints. */
  translateX: number
  /** Usa espaçamento mínimo + pan em vez de posição proporcional. */
  usesPannedLayout: boolean
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

/**
 * Distribui checkpoints com gap mínimo entre centros e desloca o viewport
 * para manter o próximo marco visível em barras estreitas (ex.: 15 marcos no mobile).
 *
 * A trilha de fill continua proporcional ao progresso real; só a camada de marcos
 * usa este layout.
 */
export function computeCheckpointLayout({
  targets,
  max,
  width,
  focusIndex,
  markerSizePx,
  minCenterGapPx
}: CheckpointLayoutInput): CheckpointLayout {
  const count = targets.length

  if (count === 0 || width <= 0 || max <= 0) {
    return { positions: [], translateX: 0, usesPannedLayout: false }
  }

  const proportional = targets.map(target =>
    clamp((target / max) * width, markerSizePx / 2, width - markerSizePx / 2)
  )

  let needsPannedLayout = false
  for (let index = 1; index < count; index += 1) {
    const gap = proportional[index] - proportional[index - 1]
    if (gap < minCenterGapPx) {
      needsPannedLayout = true
      break
    }
  }

  const spreadIfMinGap = proportional[0] + minCenterGapPx * (count - 1)
  if (spreadIfMinGap > width - markerSizePx / 2) {
    needsPannedLayout = true
  }

  if (!needsPannedLayout) {
    return {
      positions: proportional,
      translateX: 0,
      usesPannedLayout: false
    }
  }

  const positions = [...proportional]
  for (let index = 1; index < count; index += 1) {
    positions[index] = Math.max(
      positions[index],
      positions[index - 1] + minCenterGapPx
    )
  }

  const half = markerSizePx / 2
  const contentStart = positions[0] - half
  const contentEnd = positions[count - 1] + half
  const contentWidth = contentEnd - contentStart

  const safeFocus = clamp(focusIndex, 0, count - 1)
  const focusAnchor = width * 0.36

  let translateX =
    contentWidth > width
      ? focusAnchor - positions[safeFocus]
      : (width - contentEnd + contentStart) / 2 - contentStart

  const minTranslate = width - contentEnd
  const maxTranslate = -contentStart
  translateX = clamp(translateX, minTranslate, maxTranslate)

  return {
    positions,
    translateX,
    usesPannedLayout: true
  }
}
